/**
 * EmailMailboxes.tsx — customer-facing mailbox + alias management.
 *
 * Routes:
 *   /dashboard/email/mailboxes/:domainId       (v1: customer_mail_domains)
 *   /dashboard/email/mailboxes/v2/:id          (v2: mx1_mail_domains)
 *
 * Works for both hosting-coupled and email-only customers. Backend
 * routes are scoped purely by mail-domain ownership, so we don't care
 * whether there's a hosting account underneath.
 *
 * Tabs: Mailboxes · Aliases · Groups (v2) · Delivery log (v2). The tab is
 * mirrored into the URL hash (#groups) so the dashboard can deep-link.
 *
 * Features:
 *   - Create mailbox (local-part + password + quota) with the password
 *     policy checklist / generator; bulk CSV import with a weak-password
 *     preflight (v2)
 *   - Per-mailbox storage / message count / last login (v2, 60s visible-only
 *     polling) and a domain total
 *   - Suspend / reactivate, password reset, quota change, vacation
 *     autoresponder, delete
 *   - Aliases + catch-all, with the send-as note
 *   - Groups / distribution lists (v2)
 *   - Delivery log (v2)
 *
 * Colour policy: customer surface → logo palette only (navy + yellow +
 * white + grays). State indicators use brand-yellow / navy intensity +
 * icons; no red/green.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import DashShell from '../components/dash/DashShell';
import {
  api, fetchUsage, friendlyError as baseFriendlyError, MailboxUsage, sumUsage, useEscape, useVisiblePolling,
} from '../components/email/emailApi';
import { DomainUsageTotal, MailboxUsageRow } from '../components/email/UsageBar';
import { PasswordField, checkPassword, usePasswordPolicy } from '../components/email/PasswordField';
import { PasswordPolicy } from '../components/email/emailApi';
import { GroupsPanel } from '../components/email/GroupsPanel';
import { DeliveryLog } from '../components/email/DeliveryLog';

/** Absolute ceiling if the subscription's per-mailbox storage is unknown. */
const HARD_MAX_QUOTA_MB = 102400;
const MIN_QUOTA_MB = 64;
const USAGE_POLL_MS = 60_000;
/** v1 (hosting-bundle) mail keeps its old 8-char floor; v2 enforces the policy. */
const V1_MIN_PASSWORD = 8;

interface Mailbox {
  id: number;
  domain: string;
  email: string;
  active: boolean;
  quota_mb: number;
  autoresponder_enabled: boolean;
  autoresponder_subject: string | null;
  autoresponder_body: string | null;
  created_at: string;
}

interface Alias {
  id: number;
  domain_id: number;
  source: string;        // full email
  destination: string;   // full email
  created_at: string;
}

interface DomainSummary { id: string; domain: string; }

/** Subset of /email/subscriptions we need for the quota cap. */
interface SubSummary {
  id: string;
  status: string;
  storage_per_mailbox_gb?: number | null;
  max_aliases?: number | null;
  mx1_mail_domain_row_id?: string | null;
  customer_mail_domain_id?: string | null;
}

type Tab = 'mailboxes' | 'aliases' | 'groups' | 'delivery';
const TAB_IDS: Tab[] = ['mailboxes', 'aliases', 'groups', 'delivery'];

/** Map an API failure to a message the customer can act on. */
const friendlyError = (e: any, fallback: string): string => {
  if (e?.status === 409) return e?.message || 'That address already exists — pick a different name or edit the existing mailbox.';
  return baseFriendlyError(e, fallback);
};

const fmtAgo = (iso: string) => {
  const diff = Date.now() - new Date(iso).getTime();
  const d = Math.floor(diff / 86400000);
  if (d < 1) return 'today';
  if (d < 30) return `${d}d ago`;
  return new Date(iso).toLocaleDateString();
};

const readHashTab = (hash: string, allowed: Tab[]): Tab | null => {
  const h = (hash || '').replace(/^#/, '') as Tab;
  return allowed.includes(h) ? h : null;
};

const EmailMailboxes: React.FC = () => {
  // Two route shapes:
  //   /dashboard/email/mailboxes/:domainId       (v1: customer_mail_domains)
  //   /dashboard/email/mailboxes/v2/:id          (v2: mx1_mail_domains)
  // useParams gives us :domainId or :id. useLocation tells us which path
  // matched so we hit the right backend endpoints. The two endpoint families
  // are completely separate — v1 still serves hosting-bundle mail; v2 only
  // serves Business Email v2 customers on mx1.
  const params = useParams<{ domainId?: string; id?: string }>();
  const domainId = params.domainId || params.id;
  const location = useLocation();
  const isV2 = location.pathname.includes('/mailboxes/v2/');
  const mbBase = isV2 ? `/email/setup/v2/domains/${domainId}` : `/email/domains/${domainId}`;
  const domainListEndpoint = isV2 ? '/email/setup/v2' : '/email/setup';
  const connectPath = isV2 ? `/dashboard/email/connect/v2/${domainId}` : null;

  const tabs = useMemo<Array<{ id: Tab; label: string }>>(() => [
    { id: 'mailboxes', label: 'Mailboxes' },
    { id: 'aliases', label: 'Aliases' },
    ...(isV2 ? [{ id: 'groups' as Tab, label: 'Groups' }, { id: 'delivery' as Tab, label: 'Delivery log' }] : []),
  ], [isV2]);
  const allowedTabs = useMemo(() => tabs.map(t => t.id), [tabs]);

  const [domain, setDomain] = useState<DomainSummary | null>(null);
  const [mailboxes, setMailboxes] = useState<Mailbox[]>([]);
  const [aliases, setAliases] = useState<Alias[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<Mailbox | null>(null);
  // Per-mailbox storage cap from the subscription (GB → MB). Null until
  // the subscription is loaded; falls back to the hard ceiling.
  const [quotaCapMb, setQuotaCapMb] = useState<number | null>(null);
  const [maxAliases, setMaxAliases] = useState<number | null>(null);
  const [subStatus, setSubStatus] = useState<string | null>(null);
  const [usage, setUsage] = useState<MailboxUsage[] | null>(null);
  const [usageErr, setUsageErr] = useState<string | null>(null);
  const [tab, setTab] = useState<Tab>(() => readHashTab(location.hash, TAB_IDS) || 'mailboxes');
  const tabRefs = useRef<Array<HTMLButtonElement | null>>([]);
  const { policy } = usePasswordPolicy(isV2 ? domainId : null);

  const load = useCallback(async () => {
    if (!domainId) return;
    try {
      const [domR, mbR, alR] = await Promise.all([
        api<{ success: boolean; data: DomainSummary[] }>(domainListEndpoint).catch(() => null),
        api<{ success: boolean; data: Mailbox[]; error?: string }>(`${mbBase}/mailboxes`),
        api<{ success: boolean; data: Alias[]; error?: string }>(`${mbBase}/aliases`),
      ]);
      if (domR?.success && Array.isArray(domR.data)) {
        const d = domR.data.find((x) => x.id === domainId);
        if (d) setDomain(d);
      }
      if (mbR.success) setMailboxes(Array.isArray(mbR.data) ? mbR.data : []);
      else if (mbR.error) setError(mbR.error);
      if (alR.success) setAliases(Array.isArray(alR.data) ? alR.data : []);
      if (mbR.success && alR.success) setError(null);
    } catch (e: any) {
      setError(friendlyError(e, 'Couldn\'t load mailboxes'));
    } finally {
      setLoading(false);
    }
  }, [domainId, domainListEndpoint, mbBase]);

  // Storage / last-login per mailbox — v2 only. Advisory, so failures
  // never block the page; the rows just say "usage not available".
  const loadUsage = useCallback(async () => {
    if (!domainId || !isV2) return;
    try {
      const rows = await fetchUsage(domainId);
      setUsage(Array.isArray(rows) ? rows : []);
      setUsageErr(null);
    } catch (e: any) {
      setUsageErr(friendlyError(e, 'Usage is temporarily unavailable'));
      setUsage(prev => prev ?? []);
    }
  }, [domainId, isV2]);

  // Quota cap comes from the Business Email subscription that owns this
  // domain row. Hosting-bundle (v1) domains without a subscription just
  // keep the hard ceiling.
  const loadSubscription = useCallback(async () => {
    if (!domainId) return;
    try {
      const r = await api<{ success: boolean; data: SubSummary[] }>('/email/subscriptions');
      if (!r.success || !Array.isArray(r.data)) return;
      const sub = r.data.find(x =>
        (isV2 && x.mx1_mail_domain_row_id === domainId) ||
        (!isV2 && x.customer_mail_domain_id === domainId));
      if (sub) {
        setSubStatus(sub.status || null);
        const gb = Number(sub.storage_per_mailbox_gb);
        if (Number.isFinite(gb) && gb > 0) setQuotaCapMb(Math.min(HARD_MAX_QUOTA_MB, Math.round(gb * 1024)));
        const ma = Number(sub.max_aliases);
        setMaxAliases(Number.isFinite(ma) && ma > 0 ? ma : null);
      }
    } catch { /* cap is advisory — the server enforces it anyway */ }
  }, [domainId, isV2]);

  useEffect(() => { load(); loadSubscription(); loadUsage(); }, [load, loadSubscription, loadUsage]);

  // Polling is light here — mailbox state changes only when the
  // customer presses a button. 60s keeps the page fresh if there's an
  // admin action elsewhere, and we skip ticks while the tab is hidden.
  useEffect(() => {
    const t = setInterval(() => {
      if (typeof document !== 'undefined' && document.visibilityState !== 'visible') return;
      load();
    }, 60_000);
    return () => clearInterval(t);
  }, [load]);
  useVisiblePolling(loadUsage, USAGE_POLL_MS, isV2);

  // Keep the tab in the URL hash (replace, not push) so links like
  // …/mailboxes/v2/:id#groups work and Back doesn't walk through tabs.
  useEffect(() => {
    if (!allowedTabs.includes(tab)) { setTab('mailboxes'); return; }
    try { window.history.replaceState(null, '', `#${tab}`); } catch { /* ignore */ }
  }, [tab, allowedTabs]);
  useEffect(() => {
    const t = readHashTab(location.hash, allowedTabs);
    if (t) setTab(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.hash]);

  const onChanged = useCallback(() => { load(); loadUsage(); }, [load, loadUsage]);

  const onTabKey = (e: React.KeyboardEvent, idx: number) => {
    const n = tabs.length;
    let next: number | null = null;
    if (e.key === 'ArrowRight') next = (idx + 1) % n;
    else if (e.key === 'ArrowLeft') next = (idx - 1 + n) % n;
    else if (e.key === 'Home') next = 0;
    else if (e.key === 'End') next = n - 1;
    if (next === null) return;
    e.preventDefault();
    setTab(tabs[next].id);
    tabRefs.current[next]?.focus();
  };

  const effectiveCapMb = quotaCapMb ?? HARD_MAX_QUOTA_MB;
  const writesLocked = subStatus === 'suspended' || subStatus === 'cancelled';
  const usageById = useMemo(() => {
    const m = new Map<number, MailboxUsage>();
    const byEmail = new Map<string, MailboxUsage>();
    for (const u of usage || []) { m.set(Number(u.id), u); byEmail.set(String(u.email).toLowerCase(), u); }
    return (mb: Mailbox) => m.get(Number(mb.id)) || byEmail.get(mb.email.toLowerCase()) || null;
  }, [usage]);
  const totals = useMemo(() => sumUsage(usage), [usage]);

  if (loading) {
    return (
      <DashShell title="Mailboxes" subtitle="Loading…">
        <div className="h-28 rounded-2xl bg-white/60 backdrop-blur animate-pulse mb-6" />
        <div className="h-48 rounded-2xl bg-white/60 backdrop-blur animate-pulse" />
      </DashShell>
    );
  }

  const headerRight = domain && (
    <div className="flex flex-wrap gap-2">
      {connectPath && (
        <Link
          to={connectPath}
          className="text-sm bg-secondary-500 text-primary-navy font-medium px-3 py-1.5 rounded-lg hover:bg-secondary-600 transition"
        >
          Connect your devices →
        </Link>
      )}
      <Link
        to={isV2 ? `/dashboard/email/migrate/v2/${domainId}` : `/dashboard/email/migrate/${domainId}`}
        className="text-sm bg-white/80 backdrop-blur border border-white/40 text-primary-navy px-3 py-1.5 rounded-lg hover:border-secondary-500 transition"
      >
        Migrate mail from another provider →
      </Link>
    </div>
  );

  return (
    <DashShell
      title={domain?.domain ? `Mailboxes — ${domain.domain}` : 'Mailboxes'}
      subtitle="Create + manage user mailboxes. Each one can be used via webmail or any IMAP/SMTP client."
      right={headerRight}
    >
      <div className="space-y-6">
        <div>
          <Link to="/customer/email" className="text-sm text-primary-navy hover:underline">
            ← Email dashboard
          </Link>
        </div>

        {error && (
          <div role="alert" className="bg-secondary-100 border border-secondary-500 rounded-lg p-3 text-sm text-primary-navy">
            🚨 {error}
          </div>
        )}

        {writesLocked && (
          <div role="status" className="bg-secondary-100 border border-secondary-500 rounded-lg p-3 text-sm text-primary-navy">
            ⚠ This subscription is <strong>{subStatus}</strong>. Mailboxes can be viewed but not changed until it's reactivated —
            see <Link to="/dashboard/billing" className="underline">Billing</Link> or <Link to="/contact" className="underline">contact support</Link>.
          </div>
        )}

        {/* Domain storage total (v2) */}
        {isV2 && usage && usage.length > 0 && (
          <DomainUsageTotal usedMb={totals.used} quotaMb={totals.quota} mailboxes={usage.length} messages={totals.messages} title={`Storage — ${domain?.domain || 'domain'}`} />
        )}
        {isV2 && usageErr && (
          <div role="status" className="text-xs text-gray-500">⚠ {usageErr}</div>
        )}

        {/* Tabs */}
        <div role="tablist" aria-label="Mail management" className="flex flex-wrap gap-1 bg-white/80 backdrop-blur border border-white/40 rounded-xl p-1 shadow-sm">
          {tabs.map((t, i) => {
            const active = tab === t.id;
            return (
              <button
                key={t.id}
                ref={el => { tabRefs.current[i] = el; }}
                role="tab"
                id={`mbtab-${t.id}`}
                aria-selected={active}
                aria-controls={`mbpanel-${t.id}`}
                tabIndex={active ? 0 : -1}
                onClick={() => setTab(t.id)}
                onKeyDown={e => onTabKey(e, i)}
                className={`text-sm px-3 py-1.5 rounded-lg transition ${active ? 'bg-primary-navy text-white' : 'text-primary-navy hover:bg-secondary-50'}`}
              >
                {t.label}
                {t.id === 'mailboxes' && <span className="ml-1 opacity-70">({mailboxes.length})</span>}
                {t.id === 'aliases' && <span className="ml-1 opacity-70">({aliases.filter(a => !a.source.startsWith('@')).length})</span>}
              </button>
            );
          })}
        </div>

        {tab === 'mailboxes' && (
          <div role="tabpanel" id="mbpanel-mailboxes" aria-labelledby="mbtab-mailboxes" className="space-y-6">
            {/* CREATE form */}
            <CreateMailbox
              apiBase={mbBase}
              domain={domain?.domain}
              capMb={effectiveCapMb}
              policy={policy}
              enforcePolicy={isV2}
              locked={writesLocked}
              onCreated={onChanged}
            />

            {isV2 && !writesLocked && (
              <BulkImport apiBase={mbBase} domain={domain?.domain} capMb={effectiveCapMb} policy={policy} existing={mailboxes.map(m => m.email.toLowerCase())} onDone={onChanged} />
            )}

            {/* LIST */}
            <div className="bg-white/80 backdrop-blur rounded-2xl border border-white/40 shadow-sm p-5">
              <h3 className="text-lg font-semibold text-slate-900 mb-3">
                Mailboxes <span className="text-sm text-slate-500 font-normal">({mailboxes.length})</span>
              </h3>
              {mailboxes.length === 0 ? (
                <div className="text-sm text-slate-500 py-6 text-center">No mailboxes yet — create one above.</div>
              ) : (
                <ul className="divide-y divide-gray-100">
                  {mailboxes.map(m => (
                    <MailboxRow
                      key={m.id}
                      m={m}
                      apiBase={mbBase}
                      usage={isV2 ? usageById(m) : undefined}
                      usageLoading={isV2 && usage === null}
                      onEdit={() => setEditing(m)}
                      onChanged={onChanged}
                    />
                  ))}
                </ul>
              )}
            </div>
          </div>
        )}

        {tab === 'aliases' && (
          <div role="tabpanel" id="mbpanel-aliases" aria-labelledby="mbtab-aliases" className="space-y-4">
            <AliasesPanel apiBase={mbBase} domain={domain?.domain} aliases={aliases} onChanged={onChanged} connectPath={connectPath} />
          </div>
        )}

        {tab === 'groups' && isV2 && domainId && (
          <div role="tabpanel" id="mbpanel-groups" aria-labelledby="mbtab-groups">
            <GroupsPanel
              domainRowId={domainId}
              domain={domain?.domain || ''}
              aliasCount={aliases.filter(a => !a.source.startsWith('@')).length}
              maxAliases={maxAliases}
              locked={writesLocked}
              connectPath={connectPath || undefined}
              onChanged={onChanged}
            />
          </div>
        )}

        {tab === 'delivery' && isV2 && domainId && (
          <div role="tabpanel" id="mbpanel-delivery" aria-labelledby="mbtab-delivery">
            <DeliveryLog domainRowId={domainId} />
          </div>
        )}

        {editing && (
          <EditMailboxModal
            mailbox={editing}
            apiBase={mbBase}
            capMb={effectiveCapMb}
            policy={policy}
            enforcePolicy={isV2}
            onClose={() => setEditing(null)}
            onSaved={() => { setEditing(null); onChanged(); }}
          />
        )}
      </div>
    </DashShell>
  );
};

// ----------------------------------------------------------------------------
// Create mailbox

const gbLabel = (mb: number) => (mb >= 1024 ? (mb / 1024).toFixed(mb % 1024 === 0 ? 0 : 1) : (mb / 1024).toFixed(2));

const CreateMailbox: React.FC<{
  apiBase: string;
  domain?: string;
  capMb: number;
  policy: PasswordPolicy;
  enforcePolicy: boolean;
  locked: boolean;
  onCreated: () => void;
}> = ({ apiBase, domain, capMb, policy, enforcePolicy, locked, onCreated }) => {
  const [localPart, setLocalPart] = useState('');
  const [password, setPassword] = useState('');
  // Default to the plan's per-mailbox storage; the input is capped at it.
  const [quotaGb, setQuotaGb] = useState(gbLabel(capMb));
  const [touched, setTouched] = useState(false);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  // If the cap arrives after first render and the customer hasn't typed,
  // follow it.
  useEffect(() => { if (!touched) setQuotaGb(gbLabel(capMb)); }, [capMb, touched]);

  const capGb = capMb / 1024;
  const check = checkPassword(password, policy, { localPart });
  const passwordOk = enforcePolicy ? check.ok : password.length >= V1_MIN_PASSWORD;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErr(null);
    if (!passwordOk) {
      setErr(enforcePolicy ? `Password doesn't meet the policy: ${check.problems.join('; ')}.` : `Password must be at least ${V1_MIN_PASSWORD} characters.`);
      return;
    }
    setBusy(true);
    const requestedMb = Math.round(parseFloat(quotaGb) * 1024) || capMb;
    if (requestedMb > capMb) {
      setErr(`Quota can't exceed your plan's ${gbLabel(capMb)} GB per mailbox.`);
      setBusy(false);
      return;
    }
    try {
      const r = await api(`${apiBase}/mailboxes`, {
        method: 'POST',
        body: JSON.stringify({
          localPart: localPart.trim().toLowerCase(),
          password,
          quotaMb: Math.max(MIN_QUOTA_MB, Math.min(capMb, requestedMb)),
        }),
      });
      if (!r.success) { setErr(r.error || 'Failed'); return; }
      setLocalPart(''); setPassword(''); setQuotaGb(gbLabel(capMb)); setTouched(false);
      onCreated();
    } catch (e: any) {
      setErr(friendlyError(e, 'Couldn\'t create the mailbox'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <form onSubmit={submit} className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5">
      <h3 className="text-lg font-semibold text-primary-navy mb-3">Create mailbox</h3>
      <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
        <div className="md:col-span-5">
          <label htmlFor="mb-localpart" className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Email address</label>
          <div className="flex">
            <input
              id="mb-localpart"
              required
              value={localPart}
              onChange={e => setLocalPart(e.target.value)}
              placeholder="user"
              disabled={locked}
              aria-label="Mailbox name (the part before @)"
              className="w-full px-3 py-2 border border-gray-300 rounded-l-lg text-primary-navy font-mono focus:outline-none focus:ring-2 focus:ring-secondary-500 disabled:bg-gray-50"
            />
            <span className="inline-flex items-center px-3 border border-l-0 border-gray-300 rounded-r-lg bg-gray-50 text-sm text-gray-600 font-mono">
              @{domain || 'domain'}
            </span>
          </div>
        </div>
        <div className="md:col-span-4">
          <label htmlFor="mb-quota" className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Quota (GB, max {gbLabel(capMb)})</label>
          <input
            id="mb-quota"
            type="number"
            min={MIN_QUOTA_MB / 1024}
            max={capGb}
            step="0.5"
            value={quotaGb}
            disabled={locked}
            onChange={e => { setTouched(true); setQuotaGb(e.target.value); }}
            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-primary-navy font-mono focus:outline-none focus:ring-2 focus:ring-secondary-500 disabled:bg-gray-50"
          />
        </div>
        <div className="md:col-span-3 flex items-end">
          <button
            type="submit"
            disabled={busy || locked || !localPart || !passwordOk}
            className="w-full bg-secondary-500 text-primary-navy font-medium px-3 py-2 rounded-lg hover:bg-secondary-600 disabled:opacity-50 transition"
          >
            {busy ? 'Creating…' : 'Create mailbox'}
          </button>
        </div>
        <div className="md:col-span-12">
          <PasswordField
            id="mb-password"
            value={password}
            onChange={setPassword}
            policy={policy}
            localPart={localPart}
            required
            disabled={locked}
            placeholder={enforcePolicy ? undefined : `at least ${V1_MIN_PASSWORD} characters`}
          />
        </div>
      </div>
      {err && <div role="alert" className="mt-3 text-sm text-primary-navy bg-secondary-100 border border-secondary-500 rounded-lg p-2">🚨 {err}</div>}
    </form>
  );
};

// ----------------------------------------------------------------------------
// Bulk CSV import (v2) with weak-password preflight

interface BulkRow {
  line: number;
  localPart: string;
  password: string;
  quotaMb?: number;
  problems: string[];
  strength: string;
}
interface BulkResult { localPart: string; ok: boolean; error?: string }

function parseBulk(text: string, policy: PasswordPolicy, existing: string[], domain: string | undefined, capMb: number): BulkRow[] {
  const rows: BulkRow[] = [];
  const seen = new Set<string>();
  const lines = text.split(/\r?\n/);
  lines.forEach((raw, i) => {
    const line = raw.trim();
    if (!line || line.startsWith('#') || line.startsWith('//')) return;
    const parts = line.split(',').map(s => s.trim());
    const problems: string[] = [];
    let localPart = (parts[0] || '').toLowerCase();
    if (localPart.includes('@')) localPart = localPart.split('@')[0];
    const password = parts[1] || '';
    const quotaRaw = parts[2];
    const quotaMb = quotaRaw ? Number(quotaRaw) : undefined;
    if (parts.length < 2) problems.push('need localpart,password');
    if (!/^[a-z0-9][a-z0-9._-]*$/.test(localPart)) problems.push('invalid name');
    if (seen.has(localPart)) problems.push('duplicate in this list');
    seen.add(localPart);
    if (domain && existing.includes(`${localPart}@${domain}`.toLowerCase())) problems.push('mailbox already exists');
    if (quotaRaw && (!Number.isFinite(quotaMb) || (quotaMb as number) < MIN_QUOTA_MB)) problems.push(`quota must be ≥ ${MIN_QUOTA_MB} MB`);
    if (quotaMb && quotaMb > capMb) problems.push(`quota above plan cap (${capMb} MB)`);
    const check = checkPassword(password, policy, { localPart });
    if (!check.ok) problems.push(`weak password: ${check.problems.join('; ')}`);
    rows.push({ line: i + 1, localPart, password, quotaMb: quotaMb && Number.isFinite(quotaMb) ? quotaMb : undefined, problems, strength: password ? check.label : '—' });
  });
  return rows;
}

const BulkImport: React.FC<{
  apiBase: string;
  domain?: string;
  capMb: number;
  policy: PasswordPolicy;
  existing: string[];
  onDone: () => void;
}> = ({ apiBase, domain, capMb, policy, existing, onDone }) => {
  const [open, setOpen] = useState(false);
  const [text, setText] = useState('');
  const [rows, setRows] = useState<BulkRow[] | null>(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState<BulkResult[] | null>(null);
  const [err, setErr] = useState<string | null>(null);

  const preflight = () => { setRows(parseBulk(text, policy, existing, domain, capMb)); setReport(null); setErr(null); };
  const flagged = (rows || []).filter(r => r.problems.length > 0);
  const ready = (rows || []).filter(r => r.problems.length === 0);

  const dropFlagged = () => {
    const keep = new Set(ready.map(r => r.line));
    const kept = text.split(/\r?\n/).filter((l, i) => {
      const t = l.trim();
      if (!t || t.startsWith('#') || t.startsWith('//')) return true;
      return keep.has(i + 1);
    });
    const next = kept.join('\n');
    setText(next);
    setRows(parseBulk(next, policy, existing, domain, capMb));
  };

  const onFile = (f: File) => {
    const reader = new FileReader();
    reader.onload = () => { setText(String(reader.result || '')); setRows(null); setReport(null); };
    reader.readAsText(f);
  };

  const submit = async () => {
    if (ready.length === 0 || flagged.length > 0) return;
    setBusy(true);
    setErr(null);
    try {
      const r = await api<{ success: boolean; data?: { results?: BulkResult[] }; error?: string }>(`${apiBase}/mailboxes/bulk`, {
        method: 'POST',
        body: JSON.stringify({ mailboxes: ready.map(x => ({ localPart: x.localPart, password: x.password, ...(x.quotaMb ? { quotaMb: Math.min(capMb, x.quotaMb) } : {}) })) }),
      });
      if (!r.success) { setErr(r.error || 'Import failed'); return; }
      setReport(Array.isArray(r.data?.results) ? r.data!.results! : []);
      setText('');
      setRows(null);
      onDone();
    } catch (e: any) {
      setErr(friendlyError(e, 'Import failed'));
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm">
      <button type="button" onClick={() => setOpen(o => !o)} aria-expanded={open} aria-controls="bulk-import-panel" className="w-full text-left px-5 py-3 text-sm font-semibold text-primary-navy flex items-center gap-2">
        <span aria-hidden className={`inline-block transition-transform ${open ? 'rotate-90' : ''}`}>▸</span> Bulk-create mailboxes from CSV
      </button>
      {open && (
        <div id="bulk-import-panel" className="px-5 pb-5 space-y-3">
          <p className="text-xs text-gray-600">
            One mailbox per line: <code className="font-mono">name,password</code> or <code className="font-mono">name,password,quota_mb</code>.
            Lines starting with <code className="font-mono">#</code> are ignored. Every password is checked against the policy before anything is created.
          </p>
          <label htmlFor="bulk-text" className="sr-only">CSV rows</label>
          <textarea
            id="bulk-text"
            value={text}
            onChange={e => { setText(e.target.value); setRows(null); }}
            rows={6}
            spellCheck={false}
            placeholder={`alice,Correct-Horse-7-Battery\nbob,Staple-Lamp!2026,2048`}
            className="w-full font-mono text-xs px-3 py-2 border border-gray-300 rounded-lg text-primary-navy focus:outline-none focus:ring-2 focus:ring-secondary-500"
          />
          <div className="flex flex-wrap items-center gap-2">
            <label className="text-xs text-primary-navy cursor-pointer">
              <input type="file" accept=".csv,text/csv,text/plain" className="sr-only" onChange={e => { const f = e.target.files?.[0]; if (f) onFile(f); }} />
              <span className="inline-block px-3 py-1.5 rounded-lg border border-gray-300 hover:bg-gray-50">Choose CSV…</span>
            </label>
            <button type="button" onClick={preflight} disabled={!text.trim()} className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-primary-navy hover:bg-gray-50 disabled:opacity-50">
              Check rows
            </button>
            {flagged.length > 0 && (
              <button type="button" onClick={dropFlagged} className="text-xs px-3 py-1.5 rounded-lg border border-gray-300 text-primary-navy hover:bg-gray-50">
                Drop {flagged.length} flagged row{flagged.length === 1 ? '' : 's'}
              </button>
            )}
            <button type="button" onClick={submit} disabled={busy || !rows || ready.length === 0 || flagged.length > 0}
              className="text-xs px-3 py-1.5 rounded-lg bg-secondary-500 text-primary-navy font-medium hover:bg-secondary-600 disabled:opacity-50">
              {busy ? 'Creating…' : `Create ${ready.length || ''} mailbox${ready.length === 1 ? '' : 'es'}`}
            </button>
          </div>

          {rows && rows.length === 0 && <div className="text-xs text-gray-500">No rows found.</div>}
          {rows && rows.length > 0 && (
            <div className="overflow-x-auto">
              <div role="status" className="text-xs text-primary-navy mb-1">
                {ready.length} ready · {flagged.length} flagged{flagged.length > 0 ? ' — fix or drop the flagged rows to continue' : ''}
              </div>
              <table className="w-full text-xs">
                <thead>
                  <tr className="text-left text-gray-500 border-b border-gray-200">
                    <th scope="col" className="py-1 pr-2 font-medium">Line</th>
                    <th scope="col" className="py-1 pr-2 font-medium">Mailbox</th>
                    <th scope="col" className="py-1 pr-2 font-medium">Strength</th>
                    <th scope="col" className="py-1 pr-2 font-medium">Quota</th>
                    <th scope="col" className="py-1 font-medium">Status</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {rows.map(r => (
                    <tr key={r.line} className={r.problems.length ? 'bg-secondary-50' : ''}>
                      <td className="py-1 pr-2 text-gray-500">{r.line}</td>
                      <td className="py-1 pr-2 font-mono text-primary-navy break-all">{r.localPart || '—'}@{domain || 'domain'}</td>
                      <td className="py-1 pr-2 text-primary-navy">{r.strength}</td>
                      <td className="py-1 pr-2 text-primary-navy">{r.quotaMb ? `${r.quotaMb} MB` : 'default'}</td>
                      <td className="py-1 text-primary-navy">
                        {r.problems.length ? <><span aria-hidden>⚠ </span>{r.problems.join('; ')}</> : <><span aria-hidden>✓ </span>ready</>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {err && <div role="alert" className="text-xs text-primary-navy bg-secondary-100 border border-secondary-500 rounded-lg p-2">🚨 {err}</div>}
          {report && (
            <div role="status" className="text-xs space-y-1">
              <div className="font-medium text-primary-navy">{report.filter(r => r.ok).length} created, {report.filter(r => !r.ok).length} failed</div>
              {report.some(r => !r.ok) && (
                <ul className="border border-gray-200 rounded-lg p-2 bg-gray-50 max-h-40 overflow-y-auto">
                  {report.filter(r => !r.ok).map((r, i) => <li key={i} className="font-mono text-primary-navy">✗ {r.localPart}{r.error ? ` — ${r.error}` : ''}</li>)}
                </ul>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

// ----------------------------------------------------------------------------
// Mailbox row

const MailboxRow: React.FC<{
  m: Mailbox;
  apiBase: string;
  usage?: MailboxUsage | null;
  usageLoading?: boolean;
  onEdit: () => void;
  onChanged: () => void;
}> = ({ m, apiBase, usage, usageLoading, onEdit, onChanged }) => {
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const toggle = async () => {
    setBusy(true);
    setErr(null);
    try {
      await api(`${apiBase}/mailboxes/${m.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: !m.active }),
      });
      onChanged();
    } catch (e: any) {
      setErr(friendlyError(e, m.active ? 'Couldn\'t suspend the mailbox' : 'Couldn\'t reactivate the mailbox'));
    } finally { setBusy(false); }
  };

  const remove = async () => {
    if (!window.confirm(`Permanently delete ${m.email}? This removes the mailbox AND all stored mail.`)) return;
    setBusy(true);
    setErr(null);
    try {
      await api(`${apiBase}/mailboxes/${m.id}`, { method: 'DELETE' });
      onChanged();
    } catch (e: any) {
      setErr(friendlyError(e, 'Couldn\'t delete the mailbox'));
    } finally { setBusy(false); }
  };

  const quotaGb = m.quota_mb ? (m.quota_mb / 1024).toFixed(m.quota_mb >= 1024 ? 0 : 1) : '0';

  return (
    <li className="py-3">
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-primary-navy break-all">{m.email}</span>
            {!m.active && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-gray-100 text-gray-600" title="Mailbox is suspended — it can't log in or receive mail">
                ⏸ suspended
              </span>
            )}
            {m.autoresponder_enabled && (
              <span className="inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded-full bg-secondary-100 text-primary-navy" title="Vacation auto-reply is on">
                ✎ vacation
              </span>
            )}
          </div>
          <div className="text-xs text-gray-500 mt-0.5">
            {quotaGb} GB quota · added {fmtAgo(m.created_at)}
          </div>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={onEdit} disabled={busy} className="text-xs text-primary-navy hover:underline disabled:opacity-50" aria-label={`Edit ${m.email}`}>
            Edit
          </button>
          <button onClick={toggle} disabled={busy} className="text-xs text-primary-navy hover:underline disabled:opacity-50" aria-label={`${m.active ? 'Suspend' : 'Reactivate'} ${m.email}`}>
            {m.active ? 'Suspend' : 'Reactivate'}
          </button>
          <button onClick={remove} disabled={busy} className="text-xs text-primary-navy hover:underline disabled:opacity-50 font-semibold" aria-label={`Delete ${m.email}`}>
            Delete
          </button>
        </div>
      </div>
      {usage !== undefined && <MailboxUsageRow u={usage} loading={usageLoading} />}
      {err && <div role="alert" className="mt-2 text-xs text-primary-navy bg-secondary-100 border border-secondary-500 rounded-lg p-2">🚨 {err}</div>}
    </li>
  );
};

// ----------------------------------------------------------------------------
// Edit modal — password / quota / autoresponder

const EditMailboxModal: React.FC<{
  mailbox: Mailbox;
  apiBase: string;
  capMb: number;
  policy: PasswordPolicy;
  enforcePolicy: boolean;
  onClose: () => void;
  onSaved: () => void;
}> = ({ mailbox, apiBase, capMb, policy, enforcePolicy, onClose, onSaved }) => {
  useEscape(onClose);
  const [password, setPassword] = useState('');
  const [quotaGb, setQuotaGb] = useState(String((mailbox.quota_mb / 1024).toFixed(mailbox.quota_mb >= 1024 ? 0 : 1)));
  const [arEnabled, setArEnabled] = useState(mailbox.autoresponder_enabled);
  const [arSubject, setArSubject] = useState(mailbox.autoresponder_subject || '');
  const [arBody, setArBody] = useState(mailbox.autoresponder_body || '');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const check = checkPassword(password, policy, { email: mailbox.email });
  const passwordOk = !password || (enforcePolicy ? check.ok : password.length >= V1_MIN_PASSWORD);

  const save = async () => {
    setErr(null);
    if (!passwordOk) {
      setErr(enforcePolicy ? `Password doesn't meet the policy: ${check.problems.join('; ')}.` : `Password must be at least ${V1_MIN_PASSWORD} characters.`);
      return;
    }
    setBusy(true);
    // Build a sparse patch: only send the fields the customer touched.
    // Backend treats undefined as "no change."
    const patch: any = {};
    if (password) patch.password = password;
    const qMb = Math.round(parseFloat(quotaGb) * 1024);
    if (qMb > capMb) {
      setErr(`Quota can't exceed your plan's ${gbLabel(capMb)} GB per mailbox.`);
      setBusy(false);
      return;
    }
    if (qMb && qMb !== mailbox.quota_mb) patch.quotaMb = Math.max(MIN_QUOTA_MB, qMb);
    if (arEnabled !== mailbox.autoresponder_enabled || arSubject !== (mailbox.autoresponder_subject || '') || arBody !== (mailbox.autoresponder_body || '')) {
      patch.autoresponder = { enabled: arEnabled, subject: arSubject, body: arBody };
    }
    if (Object.keys(patch).length === 0) { setBusy(false); onClose(); return; }
    try {
      const r = await api(`${apiBase}/mailboxes/${mailbox.id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      });
      if (!r.success) { setErr(r.error || 'Save failed'); return; }
      onSaved();
    } catch (e: any) {
      setErr(friendlyError(e, 'Save failed'));
    } finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-primary-navy/50 backdrop-blur-sm flex items-center justify-center p-4 z-50" onClick={onClose}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-mailbox-title"
        onClick={e => e.stopPropagation()}
        className="bg-white rounded-2xl border border-gray-200 shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto"
      >
        <div className="p-5 border-b border-gray-100 flex items-center justify-between">
          <h3 id="edit-mailbox-title" className="text-lg font-semibold text-primary-navy break-all">{mailbox.email}</h3>
          <button type="button" onClick={onClose} aria-label="Close" className="text-gray-400 hover:text-gray-700 text-xl leading-none">×</button>
        </div>
        <div className="p-5 space-y-4">
          <PasswordField
            id="edit-password"
            label="New password"
            value={password}
            onChange={setPassword}
            policy={policy}
            email={mailbox.email}
            placeholder="leave blank to keep current"
            quietWhenEmpty
          />
          <div>
            <label htmlFor="edit-quota" className="block text-xs uppercase tracking-wider text-gray-500 mb-1">Quota (GB, max {gbLabel(capMb)})</label>
            <input
              id="edit-quota"
              type="number"
              min={MIN_QUOTA_MB / 1024}
              max={capMb / 1024}
              step="0.5"
              value={quotaGb}
              onChange={e => setQuotaGb(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-primary-navy font-mono focus:outline-none focus:ring-2 focus:ring-secondary-500"
            />
          </div>
          <div className="border-t border-gray-100 pt-4">
            <label className="flex items-center gap-2 mb-2">
              <input type="checkbox" checked={arEnabled} onChange={e => setArEnabled(e.target.checked)} className="accent-primary-navy" />
              <span className="text-sm font-medium text-primary-navy">Vacation autoresponder</span>
            </label>
            <p className="text-xs text-gray-500 mb-3">
              Send an automatic reply to every incoming message while enabled. Each sender only gets the reply once per day.
            </p>
            <input
              value={arSubject}
              onChange={e => setArSubject(e.target.value)}
              disabled={!arEnabled}
              aria-label="Auto-reply subject"
              placeholder="Subject (e.g. Out of office)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-primary-navy mb-2 disabled:bg-gray-50 disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-secondary-500"
            />
            <textarea
              value={arBody}
              onChange={e => setArBody(e.target.value)}
              disabled={!arEnabled}
              rows={4}
              aria-label="Auto-reply body"
              placeholder="Body of the auto-reply"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-primary-navy disabled:bg-gray-50 disabled:text-gray-400 focus:outline-none focus:ring-2 focus:ring-secondary-500"
            />
          </div>
          {err && <div role="alert" className="text-sm text-primary-navy bg-secondary-100 border border-secondary-500 rounded-lg p-2">🚨 {err}</div>}
        </div>
        <div className="p-5 border-t border-gray-100 flex justify-end gap-2">
          <button type="button" onClick={onClose} className="text-sm px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 text-primary-navy">Cancel</button>
          <button onClick={save} disabled={busy || !passwordOk} className="text-sm bg-secondary-500 text-primary-navy font-medium px-4 py-2 rounded-lg hover:bg-secondary-600 disabled:opacity-50">
            {busy ? 'Saving…' : 'Save'}
          </button>
        </div>
      </div>
    </div>
  );
};

// ----------------------------------------------------------------------------
// Aliases + catchall

const AliasesPanel: React.FC<{
  apiBase: string;
  domain?: string;
  aliases: Alias[];
  onChanged: () => void;
  connectPath?: string | null;
}> = ({ apiBase, domain, aliases, onChanged, connectPath }) => {
  const [src, setSrc] = useState('');
  const [dst, setDst] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);

  const catchall = aliases.find(a => a.source.startsWith('@'));
  const regular = aliases.filter(a => !a.source.startsWith('@'));
  const [catchallDest, setCatchallDest] = useState(catchall?.destination || '');
  useEffect(() => { setCatchallDest(catchall?.destination || ''); }, [catchall?.destination]);

  const addAlias = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api(`${apiBase}/aliases`, {
        method: 'POST',
        body: JSON.stringify({
          source: src.includes('@') ? src : `${src}@${domain}`,
          destination: dst,
        }),
      });
      if (!r.success) { setErr(r.error || 'Failed'); return; }
      setSrc(''); setDst('');
      onChanged();
    } catch (e: any) {
      setErr(friendlyError(e, 'Couldn\'t add the alias'));
    } finally { setBusy(false); }
  };

  const removeAlias = async (a: Alias) => {
    if (!window.confirm(`Delete alias ${a.source}?`)) return;
    setBusy(true);
    setErr(null);
    try {
      await api(`${apiBase}/aliases/${a.id}`, { method: 'DELETE' });
      onChanged();
    } catch (e: any) {
      setErr(friendlyError(e, 'Couldn\'t delete the alias'));
    } finally { setBusy(false); }
  };

  const saveCatchall = async () => {
    setBusy(true);
    setErr(null);
    try {
      const r = await api(`${apiBase}/catchall`, {
        method: 'POST',
        body: JSON.stringify({ destination: catchallDest || null }),
      });
      if (!r.success) { setErr(r.error || 'Failed'); return; }
      onChanged();
    } catch (e: any) {
      setErr(friendlyError(e, 'Couldn\'t update the catch-all'));
    } finally { setBusy(false); }
  };

  return (
    <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-5 space-y-5">
      <div>
        <h3 className="text-lg font-semibold text-primary-navy">Forwarders &amp; aliases</h3>
        <p className="text-sm text-gray-600 mt-1">
          Aliases forward mail to another address. Useful for shared inboxes (sales@, support@) or temporary forwards.
        </p>
      </div>

      {/* Send-as note */}
      <div role="note" className="text-sm text-primary-navy/90 bg-secondary-50 border border-secondary-200 rounded-lg p-3">
        <strong>Sending from an alias:</strong> Mailboxes can send from any alias that forwards to them — add the alias as an extra
        From address / identity in your mail app.
        {connectPath && <> See <Link to={connectPath} className="underline">Connect your devices</Link> for the per-app steps.</>}
      </div>

      {/* Catch-all */}
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <div className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">Catch-all</div>
        <p className="text-xs text-gray-600 mb-2">
          Forwards mail sent to ANY address on {domain || 'this domain'} that doesn't match a mailbox or alias.
          Leave blank to disable.
        </p>
        <div className="flex flex-wrap gap-2">
          <input
            value={catchallDest}
            onChange={e => setCatchallDest(e.target.value)}
            aria-label="Catch-all destination address"
            placeholder="forward-to@example.com"
            className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-primary-navy focus:outline-none focus:ring-2 focus:ring-secondary-500"
          />
          <button
            onClick={saveCatchall}
            disabled={busy}
            className="bg-secondary-500 text-primary-navy font-medium px-4 py-2 rounded-lg hover:bg-secondary-600 disabled:opacity-50"
          >
            {catchall ? (catchallDest ? 'Update' : 'Remove') : 'Set'}
          </button>
        </div>
      </div>

      {/* Add alias */}
      <div>
        <div className="text-xs uppercase tracking-wider text-gray-500 font-medium mb-2">Add alias</div>
        <div className="grid grid-cols-1 md:grid-cols-12 gap-2">
          <div className="md:col-span-4">
            <div className="flex">
              <input
                value={src}
                onChange={e => setSrc(e.target.value)}
                aria-label="Alias name (the part before @)"
                placeholder="sales"
                className="w-full px-3 py-2 border border-gray-300 rounded-l-lg text-primary-navy font-mono focus:outline-none focus:ring-2 focus:ring-secondary-500"
              />
              <span className="inline-flex items-center px-3 border border-l-0 border-gray-300 rounded-r-lg bg-gray-50 text-sm text-gray-600 font-mono">
                @{domain || 'domain'}
              </span>
            </div>
          </div>
          <div className="md:col-span-1 flex items-center justify-center text-gray-400 text-sm" aria-hidden>→</div>
          <div className="md:col-span-6">
            <input
              value={dst}
              onChange={e => setDst(e.target.value)}
              aria-label="Alias destination (full email address)"
              placeholder="full email (any domain)"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-primary-navy focus:outline-none focus:ring-2 focus:ring-secondary-500"
            />
          </div>
          <div className="md:col-span-1">
            <button
              onClick={addAlias}
              disabled={busy || !src || !dst}
              className="w-full bg-secondary-500 text-primary-navy font-medium px-3 py-2 rounded-lg hover:bg-secondary-600 disabled:opacity-50"
            >
              Add
            </button>
          </div>
        </div>
        {err && <div role="alert" className="mt-2 text-sm text-primary-navy bg-secondary-100 border border-secondary-500 rounded-lg p-2">🚨 {err}</div>}
      </div>

      {/* List */}
      {regular.length > 0 && (
        <ul className="divide-y divide-gray-100">
          {regular.map(a => (
            <li key={a.id} className="py-2 flex items-center justify-between gap-3">
              <div className="font-mono text-sm text-primary-navy break-all">
                {a.source} <span className="text-gray-400">→</span> {a.destination}
              </div>
              <button onClick={() => removeAlias(a)} disabled={busy} className="text-xs text-primary-navy hover:underline font-semibold" aria-label={`Delete alias ${a.source}`}>
                Delete
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

export default EmailMailboxes;
